/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import Text from 'src/components/ts-components/Text'
import { useOrganizationModalContext } from '../../context'
import { TextInput } from 'src/components/ts-components/TextInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import CopyComponent from 'src/components/CopyComponent'
import { Image, Upload } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import Button from 'src/components/ts-components/Button'
import DeleteModal from 'src/components/Modal/DeleteModal'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import req from '@sportninja/common/api/request'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const DeleteButton = ({ ...props }) => {
  return (
    <button
      css={css`
        display: flex;
        padding: 8px 16px 8px 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.ERROR_200};
        box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
      `}
      {...props}
    >
      <DeleteOutlined
        css={css`
          color: ${colors.ERROR_200};
        `}
        width={24}
        height={24}
      />
      <Text variant='paragraphMedium' weight='semiBold' color={colors.WHITE}>
        DELETE {isCanlan ? 'LEAGUE' : 'ORGANIZATION'}
      </Text>
    </button>
  )
}

const GeneralSettings = () => {
  const {
    state,
    setState,
    isEdit,
    beforeUpload,
    handleChange,
    setIsFormOpen,
    refresh,
    messageApi,
    isOnOwnOrgPage,
    clearState,
  } = useOrganizationModalContext()

  return (
    <div
      css={css`
        display: flex;
        padding: 16px;
        gap: 16px;
        flex-direction: column;
        align-items: flex-end;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          General Info
        </Text>
        {isEdit ? (
          <div>
            <CopyComponent id={state.id} />
          </div>
        ) : null}
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
          align-self: stretch;
        `}
      >
        <Image
          width={56}
          height={56}
          css={css`
            border-radius: 120px;
            border: 1px solid ${colors.WHITE};
            background: ${colors.SECONDARY_200};
          `}
          src={
            state.image_url ||
            '/images/generic_placeholders/placeholder_organization.png'
          }
          preview={false}
        />
        <Upload
          beforeUpload={beforeUpload}
          onChange={handleChange}
          showUploadList={false}
          action={undefined}
        >
          <Button
            text='Upload'
            variant='primary'
            onClick={() => {
              console.log('Upload')
            }}
          />
        </Upload>
        {state.image_url ? (
          <div
            css={css`
              max-width: 100px;
            `}
          >
            <Button
              text='Remove'
              variant='danger'
              onClick={() => {
                setState({ ...state, image_url: null, image_changed: true })
              }}
            />
          </div>
        ) : null}
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          flex-direction: row;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <TextInput
            label='Name *'
            value={state.name}
            setValue={(value) => {
              setState({ ...state, name: value })
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={false}
            name='name'
          />
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <TextInput
            label='Abbreviation (up to 8 characters)*'
            value={state.abbreviation}
            setValue={(value) => {
              setState({ ...state, abbreviation: value })
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={false}
            maxLength={8}
            name='abbreviation'
          />
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          flex-direction: row;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <TextInput
            label='Phone Number'
            value={state.phone}
            setValue={(value) => {
              if (value.length > 0 && !value.match(/^\d+$/)) {
                return
              }
              setState({ ...state, phone: value })
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={false}
            name='phone'
            type='tel'
          />
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <TextInput
            label='Email'
            value={state.email}
            setValue={(value) => {
              setState({ ...state, email: value })
            }}
            onBlur={() => {
              if (state.email.length > 0 && !emailRegex.test(state.email)) {
                setState({
                  ...state,
                  fieldErrors: [...state.fieldErrors, 'email'],
                  customError: {
                    ...state.customError,
                    email: 'Invalid email',
                  },
                })
              } else {
                setState({
                  ...state,
                  fieldErrors: state.fieldErrors.filter(
                    (field) => field !== 'email'
                  ),
                  customError: {
                    ...state.customError,
                    email: '',
                  },
                })
              }
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={false}
            name='email'
            type='email'
          />
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          flex-direction: row;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <TextInput
            label='Website'
            value={state.website}
            setValue={(value) => {
              setState({ ...state, website: value })
            }}
            onBlur={() => {
              const regex =
                /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/
              if (state.website.length > 0 && !state.website.match(regex)) {
                setState({
                  ...state,
                  fieldErrors: [...state.fieldErrors, 'website'],
                  // add the field to the customError
                  customError: {
                    ...state.customError,
                    website: 'Invalid website',
                  },
                })
              } else {
                // remove the field from the fieldErrors and customError
                setState({
                  ...state,
                  fieldErrors: state.fieldErrors.filter(
                    (field) => field !== 'website'
                  ),
                  customError: {
                    ...state.customError,
                    website: '',
                  },
                })
              }
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={false}
            name='website'
            type='url'
          />
        </div>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title={`Set ${isCanlan ? 'League' : 'Organization'} as Private`}
          subtitle={`Private ${
            isCanlan ? 'Leagues' : 'Organizations'
          } can only be viewed and followed by people that have been invited by admins.`}
          value={state.is_private_organization}
          onChange={(value) => {
            setState({ ...state, is_private_organization: value })
          }}
        />
      </div>
      {isEdit ? (
        <>
          <div
            css={css`
              height: 1px;
              background-color: ${colors.SECONDARY_200};
              width: 100%;
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              width: 100%;
            `}
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Danger Zone
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Permanently delete the organization and all info related.
            </Text>

            <DeleteModal
              title='Delete Organization'
              message={`Are you sure you want to delete ${state.name}? This cannot be undone.`}
              onDelete={async (shouldForce = false) => {
                const query: any = {}
                if (shouldForce) {
                  query.force = 1
                }
                await req(`/${ENTITY_TYPES.org}/${state.id}`, {
                  method: 'DELETE',
                  query,
                })
                messageApi.success(`${state.name} has been deleted.`)
                if (isOnOwnOrgPage) {
                  window.location.href = '/orgs'
                }
                clearState()
                setIsFormOpen(false)
                refresh()
              }}
              Button={DeleteButton}
              forceDeleteText={undefined}
              shouldCloseModalAfterDelete={true}
              useForceDelete={true}
              displayBanner={false}
            />
          </div>
        </>
      ) : null}
    </div>
  )
}

export default GeneralSettings
